import {
  Box,
  Button,
  IconButton,
  ListSubheader,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { Add, Close } from "@mui/icons-material";

import {
  ColumnFilter,
  FilterableColumn,
  FilterOperator,
  OPERATOR_LABELS,
  POSITIONS,
} from "./filters";

/**
 * Filter rows: pick a column, pick value or percentile, pick a direction, type a
 * number. Deliberately not a fixed set of controls — the useful question is
 * rarely one someone anticipated when building the panel.
 *
 * Serves both views, which need different controls. The league table passes
 * `team` (row selection there) and keeps its position chips in the header. The
 * player page passes `position` instead: it has one player, so every control in
 * this panel is population-defining and belongs together — see
 * `PopulationFilters` in `filters.ts`.
 */
export default function FilterPanel({
  columns,
  filters,
  onChange,
  team,
  teams,
  onTeamChange,
  position,
  onPositionChange,
}: {
  columns: FilterableColumn[];
  filters: ColumnFilter[];
  onChange: (filters: ColumnFilter[]) => void;
  team?: string;
  teams?: string[];
  onTeamChange?: (team: string) => void;
  position?: string;
  onPositionChange?: (position: string) => void;
}) {
  const grouped = columns.reduce<Record<string, FilterableColumn[]>>(
    (acc, column) => {
      (acc[column.section] ||= []).push(column);
      return acc;
    },
    {},
  );

  const update = (index: number, patch: Partial<ColumnFilter>) => {
    onChange(filters.map((f, i) => (i === index ? { ...f, ...patch } : f)));
  };

  const columnFor = (field: string) => columns.find((c) => c.field === field);

  return (
    <Stack
      spacing={1.5}
      sx={{ px: 2, py: 1.75, minWidth: 640 }}
    >
      {onTeamChange && (
        <Stack direction="row" spacing={1.5} alignItems="center">
          <Label>Team</Label>
          <Select
            size="small"
            value={team ?? "ALL"}
            onChange={(e) => onTeamChange(e.target.value)}
            sx={{ borderRadius: 0, minWidth: 130, fontSize: 13 }}
          >
            <MenuItem value="ALL">All teams</MenuItem>
            {(teams ?? []).map((option) => (
              <MenuItem key={option} value={option}>
                {option}
              </MenuItem>
            ))}
          </Select>
        </Stack>
      )}

      {onPositionChange && (
        <Stack direction="row" spacing={1.5} alignItems="center">
          <Label>Position</Label>
          <Select
            size="small"
            value={position ?? "ALL"}
            onChange={(e) => onPositionChange(e.target.value)}
            sx={{ borderRadius: 0, minWidth: 130, fontSize: 13 }}
          >
            {POSITIONS.map((option) => (
              <MenuItem key={option} value={option}>
                {option === "ALL" ? "All positions" : option}
              </MenuItem>
            ))}
          </Select>
        </Stack>
      )}

      {filters.map((filter, index) => {
        const column = columnFor(filter.field);
        return (
          <Stack
            key={index}
            direction="row"
            spacing={1}
            alignItems="center"
            flexWrap="wrap"
            useFlexGap
          >
            <Select
              size="small"
              value={filter.field}
              onChange={(e) => {
                const next = columnFor(e.target.value);
                update(index, {
                  field: e.target.value,
                  // Identity columns have no percentile to filter on.
                  basis: next?.valueOnly ? "v" : filter.basis,
                });
              }}
              sx={{ borderRadius: 0, minWidth: 260, fontSize: 13 }}
            >
              {Object.entries(grouped).flatMap(([section, sectionColumns]) => [
                <ListSubheader key={section} sx={{ fontSize: 11, fontWeight: 700 }}>
                  {section}
                </ListSubheader>,
                ...sectionColumns.map((option) => (
                  <MenuItem key={option.field} value={option.field}>
                    {option.label}
                  </MenuItem>
                )),
              ])}
            </Select>

            <Select
              size="small"
              value={filter.basis}
              onChange={(e) =>
                update(index, { basis: e.target.value as "v" | "p" })
              }
              disabled={column?.valueOnly}
              sx={{ borderRadius: 0, minWidth: 110, fontSize: 13 }}
            >
              <MenuItem value="v">value</MenuItem>
              <MenuItem value="p">percentile</MenuItem>
            </Select>

            <Select
              size="small"
              value={filter.op}
              onChange={(e) =>
                update(index, { op: e.target.value as FilterOperator })
              }
              sx={{ borderRadius: 0, minWidth: 70, fontSize: 15 }}
            >
              {(Object.keys(OPERATOR_LABELS) as FilterOperator[]).map((op) => (
                <MenuItem key={op} value={op}>
                  {OPERATOR_LABELS[op]}
                </MenuItem>
              ))}
            </Select>

            <TextField
              size="small"
              type="number"
              value={Number.isFinite(filter.value) ? String(filter.value) : ""}
              // An empty field has to stay empty. `Number("")` is 0, so coercing
              // on every keystroke put a 0 back the moment you cleared it and
              // made the field impossible to retype. NaN carries "no value" until
              // one is entered; `applyFilters` ignores those rows.
              onChange={(e) =>
                update(index, {
                  value: e.target.value === "" ? NaN : Number(e.target.value),
                })
              }
              InputProps={{ sx: { borderRadius: 0, fontSize: 13 } }}
              sx={{ width: 110 }}
            />

            <IconButton
              size="small"
              aria-label="Remove filter"
              onClick={() => onChange(filters.filter((_f, i) => i !== index))}
            >
              <Close fontSize="small" />
            </IconButton>
          </Stack>
        );
      })}

      <Stack direction="row" spacing={1.5} alignItems="center">
        <Button
          size="small"
          startIcon={<Add />}
          onClick={() =>
            onChange([
              ...filters,
              // Starts empty rather than at 0, which would filter the table the
              // instant a row is added.
              {
                field: columns[0]?.field ?? "gp",
                basis: "v",
                op: "gte",
                value: NaN,
              },
            ])
          }
        >
          Add filter
        </Button>
        <Box flexGrow={1} />
        {(filters.length > 0 ||
          (team && team !== "ALL") ||
          (position && position !== "ALL")) && (
          <Button
            size="small"
            variant="outlined"
            onClick={() => {
              onChange([]);
              onTeamChange?.("ALL");
              onPositionChange?.("ALL");
            }}
          >
            Clear all
          </Button>
        )}
      </Stack>
    </Stack>
  );
}

function Label({ children }: { children: React.ReactNode }) {
  return (
    <Typography
      sx={{
        fontSize: 11,
        fontWeight: 700,
        letterSpacing: "0.1em",
        color: "#8a8a8a",
        textTransform: "uppercase",
      }}
    >
      {children}
    </Typography>
  );
}
